<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  recipients: {
    type: Array,
    required: true,
  },
  meta: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const statusFilter = defineModel('statusFilter', { type: String, default: '' });
const currentPage = defineModel('currentPage', { type: Number, default: 1 });

const { t } = useI18n();

const RECIPIENTS_PER_PAGE = 25;

// `sending`/`send_ambiguous`/`delivery_failed` (P0 hardening pass): an
// operator must be able to filter directly to send_ambiguous recipients -
// the one status that needs manual review and is never auto-retried.
const STATUS_OPTIONS = [
  { value: '', label: t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.ALL_STATUSES') },
  { value: 'pending', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.PENDING') },
  { value: 'queued', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.QUEUED') },
  { value: 'sending', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.SENDING') },
  { value: 'sent', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.SENT') },
  { value: 'delivered', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.DELIVERED') },
  { value: 'read', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.READ') },
  { value: 'failed', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.FAILED') },
  {
    value: 'send_ambiguous',
    label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.SEND_AMBIGUOUS'),
  },
  {
    value: 'delivery_failed',
    label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.DELIVERY_FAILED'),
  },
  { value: 'skipped', label: t('CAMPAIGN.WHATSAPP.DETAIL.STATUS.SKIPPED') },
];

const totalItems = computed(() => props.meta.count || 0);

const formatTimestamp = seconds =>
  seconds ? new Date(seconds * 1000).toLocaleString() : '';
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.TITLE') }}
      </h4>
      <ComboBox v-model="statusFilter" :options="STATUS_OPTIONS" class="w-40" />
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <BaseTable
      v-else
      :headers="[
        t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.PHONE_NUMBER'),
        t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.STATUS'),
        t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.ERROR'),
        t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.ATTEMPTS'),
        t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.SENT_AT'),
      ]"
      :items="recipients"
      :no-data-message="t('CAMPAIGN.WHATSAPP.DETAIL.RECIPIENTS.NO_RECIPIENTS')"
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="recipient in items"
          :key="recipient.id"
          :item="recipient"
        >
          <template #default>
            <BaseTableCell>{{ recipient.phone_number }}</BaseTableCell>
            <BaseTableCell class="capitalize">
              {{
                t(
                  `CAMPAIGN.WHATSAPP.DETAIL.STATUS.${recipient.status.toUpperCase()}`
                )
              }}
            </BaseTableCell>
            <BaseTableCell>{{ recipient.error_message || '-' }}</BaseTableCell>
            <BaseTableCell>{{ recipient.attempts }}</BaseTableCell>
            <BaseTableCell>
              {{ formatTimestamp(recipient.sent_at) }}
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <PaginationFooter
      v-if="totalItems > 0"
      v-model:current-page="currentPage"
      :total-items="totalItems"
      :items-per-page="RECIPIENTS_PER_PAGE"
    />
  </div>
</template>
